import React from 'react';
import { HexColorPicker } from 'react-colorful';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import { ChevronDown, ChevronUp, GripVertical, X, Plus } from 'lucide-react';
import { Color } from './types';
import { CopyButton } from './CopyButton';
import { motion } from 'framer-motion';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  generateShades,
  generateTints,
  generateTransparencies,
  getColorValue,
  getTransparencyColorValue,
  parseColorValue,
} from './utils';

interface ColorSwatchProps {
  color: Color;
  onColorChange: (id: string, updates: Partial<Color>) => void;
  onRemove: (id: string) => void;
  onAddAfter: () => void;
  index: number;
}

export const ColorSwatch = React.forwardRef<HTMLDivElement, ColorSwatchProps>(({
  color,
  onColorChange,
  onRemove,
  onAddAfter,
  index,
}, ref) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: color.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [format, setFormat] = React.useState('oklch');
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [shadesEnabled, setShadesEnabled] = React.useState(color.shadesEnabled || false);
  const [shadesCount, setShadesCount] = React.useState(color.shadesCount || 5);
  const [tintsEnabled, setTintsEnabled] = React.useState(color.tintsEnabled || false);
  const [tintsCount, setTintsCount] = React.useState(color.tintsCount || 5);
  const [transparencyEnabled, setTransparencyEnabled] = React.useState(color.transparencyEnabled || false);
  const [transparencyCount, setTransparencyCount] = React.useState(color.transparencyCount || 10);

  // Update local state when color prop changes (for loading saved presets or global format changes)
  React.useEffect(() => {
    setFormat(color.format || 'hsl');
    setShadesEnabled(color.shadesEnabled || false);
    setShadesCount(color.shadesCount || 5);
    setTintsEnabled(color.tintsEnabled || false);
    setTintsCount(color.tintsCount || 5);
    setTransparencyEnabled(color.transparencyEnabled || false);
    setTransparencyCount(color.transparencyCount || 10);
  }, [
    color.id,
    color.format,
    color.shadesEnabled,
    color.shadesCount,
    color.tintsEnabled,
    color.tintsCount,
    color.transparencyEnabled,
    color.transparencyCount
  ]);

  // Copy handled by CopyButton component

  const handleFormatChange = (newFormat: string) => {
    setFormat(newFormat);
    
    // Auto-update transparency format based on parent format
    let newTransparencyFormat: "hexa" | "rgba" | "hsla";
    if (newFormat === 'hsl') {
      newTransparencyFormat = 'hsla';
    } else if (newFormat === 'rgb') {
      newTransparencyFormat = 'rgba';
    } else {
      newTransparencyFormat = 'hexa';
    }
    
    onColorChange(color.id, { 
      format: newFormat as 'hex' | 'rgb' | 'hsl' | 'oklch',
      transparencyFormat: newTransparencyFormat
    });
  };

  const formats: ('oklch' | 'hex' | 'rgb' | 'hsl')[] = ['oklch', 'hex', 'rgb', 'hsl'];
  const handleCycleFormat = (direction: 'up' | 'down') => {
    const currentIndex = formats.indexOf(format as any);
    let newIndex = currentIndex;
    if (direction === 'up') {
      newIndex = currentIndex === 0 ? formats.length - 1 : currentIndex - 1;
    } else {
      newIndex = currentIndex === formats.length - 1 ? 0 : currentIndex + 1;
    }
    handleFormatChange(formats[newIndex]);
  };

  return (
    <motion.div
      ref={(node) => {
        setNodeRef(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as any).current = node;
      }}
      style={style}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ delay: index * 0.1 }}
      className="relative group"
    >
      {/* Add button - appears on hover, placed completely outside the left edge */}
      <div
        role="button"
        tabIndex={0}
        onClick={onAddAfter}
        className="absolute -left-6 top-[19px] p-0 h-5 w-5 bg-transparent text-muted-foreground hover:text-green-500 opacity-0 group-hover:opacity-100 transition-colors duration-200 z-10 flex items-center justify-center outline-none cursor-pointer select-none"
      >
        <Plus className="w-4 h-4" />
      </div>

      {/* Remove button - appears on hover, placed completely outside the right edge */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => onRemove(color.id)}
        className="absolute -right-6 top-[19px] p-0 h-5 w-5 bg-transparent text-muted-foreground hover:text-red-500 opacity-0 group-hover:opacity-100 transition-colors duration-200 z-10 flex items-center justify-center outline-none cursor-pointer select-none"
      >
        <X className="w-4 h-4" />
      </div>

      <div
        className={`rounded-2xl border transition-all duration-200 overflow-hidden ${
          isDragging ? 'border-primary/50 border-dashed bg-primary/5 shadow-inner' : 'border-border bg-card shadow-sm'
        }`}
      >
        <div className={isDragging ? 'opacity-0' : ''}>
          <div
          className={`grid gap-2 items-center px-2 py-3 hover:bg-muted/30 transition-colors`}
          style={{
            gridTemplateColumns: '16px 48px 1fr 32px 24px',
          }}
        >
          <div className="flex items-center justify-center min-w-0" {...attributes} {...listeners}>
          <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab active:cursor-grabbing transition-colors duration-200 hover:text-foreground" />
        </div>
        <div className="flex justify-start min-w-0">
          <Popover open={isPickerOpen} onOpenChange={setIsPickerOpen}>
            <PopoverTrigger asChild>
              <div
                className={`w-12 h-8 rounded-md border-2 border-border cursor-pointer hover:opacity-80 transition-opacity`}
                style={{ backgroundColor: color.hex }}
              />
            </PopoverTrigger>
            <PopoverContent className="w-80 p-4 bg-card border-border text-foreground" side="right">
              <HexColorPicker
                color={color.hex}
                onChange={(newColor) => onColorChange(color.id, { hex: newColor.toUpperCase() })}
                style={{ width: '100%' }}
              />
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-2 flex-1 relative">
                    <Input
                      value={getColorValue(color.hex, format)}
                      onChange={(e) => {
                        const parsedHex = parseColorValue(e.target.value, format);
                        if (parsedHex) {
                          onColorChange(color.id, { hex: parsedHex });
                        }
                      }}
                      onBlur={(e) => {
                        const parsedHex = parseColorValue(e.target.value, format);
                        if (!parsedHex) {
                          e.target.value = getColorValue(color.hex, format);
                        }
                      }}
                      placeholder={format === 'hex' ? '#000000' : format === 'rgb' ? 'rgb(0, 0, 0)' : 'hsl(0, 0%, 0%)'}
                      className="font-mono text-sm flex-1 pr-8"
                    />
                    <div className="absolute right-1">
                      <CopyButton value={getColorValue(color.hex, format)} />
                    </div>
                  </div>
                    <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-9 shrink-0 select-none">
                      <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                        {format}
                      </span>
                      <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                        <button
                          type="button"
                          onClick={() => handleCycleFormat('up')}
                          className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                          title="Previous format"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCycleFormat('down')}
                          className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors"
                          title="Next format"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>


        <div className="min-w-0">
          <Input
            value={color.variable}
            onChange={(e) => onColorChange(color.id, { variable: e.target.value })}
            placeholder="--variable"
            className="font-mono text-sm h-8 bg-background border-border rounded-lg"
          />
        </div>

        <div className="flex justify-end min-w-0">
          <CopyButton value={getColorValue(color.hex, format)} />
        </div>

        <div className="flex justify-center items-center min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 h-6 w-6 bg-transparent border-0 flex-shrink-0 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </Button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="bg-muted/20 border-t border-border p-4">
          <div className="space-y-5">
            
            {/* Shades Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={shadesEnabled || tintsEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setShadesEnabled(enabled);
                    setTintsEnabled(enabled);
                    onColorChange(color.id, { 
                      shadesEnabled: enabled,
                      shadesCount: enabled ? shadesCount : undefined,
                      tintsEnabled: enabled,
                      tintsCount: enabled ? shadesCount : undefined,
                      customShades: undefined,
                      customTints: undefined
                    });
                  }}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium whitespace-nowrap">
                  Shades
                </label>
                <Input
                  type="number"
                  min="2"
                  max="5"
                  value={shadesCount}
                  onChange={(e) => {
                    const count = Math.max(2, parseInt(e.target.value) || 5);
                    setShadesCount(count);
                    setTintsCount(count);
                    onColorChange(color.id, {
                      shadesCount: count,
                      tintsCount: count,
                      customShades: undefined,
                      customTints: undefined
                    });
                  }}
                  className="w-16 h-8 text-sm"
                  disabled={!(shadesEnabled || tintsEnabled)}
                />
              </div>
                
              {(shadesEnabled || tintsEnabled) && (
                <div className="pt-3 border-t border-border/30">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {tintsEnabled && [...(color.customTints || generateTints(color.hex, tintsCount))].reverse().map((tint, reversedIdx) => {
                      const originalIdx = (color.customTints || generateTints(color.hex, tintsCount)).length - 1 - reversedIdx;
                      return (
                        <Popover key={`tint-${originalIdx}`}>
                          <PopoverTrigger asChild>
                            <div
                              className="w-8 h-8 rounded-full border-2 border-border cursor-pointer hover:scale-110 transition-transform shadow-sm"
                              style={{ backgroundColor: tint.hex }}
                              title={`${color.variable}${tint.variable}: ${getColorValue(tint.hex, format)}`}
                            />
                          </PopoverTrigger>
                          <PopoverContent className="w-80 p-4 bg-card border-border text-foreground" side="top">
                            <HexColorPicker
                              color={tint.hex}
                              onChange={(newColor) => {
                                const updatedTints = [...(color.customTints || generateTints(color.hex, tintsCount))];
                                updatedTints[originalIdx] = { ...tint, hex: newColor.toUpperCase() };
                                onColorChange(color.id, { customTints: updatedTints });
                              }}
                              style={{ width: '100%' }}
                            />
                            <div className="mt-2 space-y-2">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-2 flex-1 relative">
                                  <Input
                                    value={getColorValue(tint.hex, format)}
                                    onChange={(e) => {
                                      const parsedHex = parseColorValue(e.target.value, format);
                                      if (parsedHex) {
                                        const updatedTints = [...(color.customTints || generateTints(color.hex, tintsCount))];
                                        updatedTints[originalIdx] = { ...tint, hex: parsedHex.toUpperCase() };
                                        onColorChange(color.id, { customTints: updatedTints });
                                      }
                                    }}
                                    onBlur={(e) => {
                                      const parsedHex = parseColorValue(e.target.value, format);
                                      if (!parsedHex) {
                                        e.target.value = getColorValue(tint.hex, format);
                                      }
                                    }}
                                    placeholder={format === 'hex' ? '#000000' : format === 'rgb' ? 'rgb(0, 0, 0)' : 'hsl(0, 0%, 0%)'}
                                    className="font-mono text-sm flex-1 pr-8"
                                  />
                                  <div className="absolute right-1">
                                    <CopyButton value={getColorValue(tint.hex, format)} />
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-9 shrink-0 select-none">
                                  <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                                    {format}
                                  </span>
                                  <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                                    <button type="button" onClick={() => handleCycleFormat('up')} className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Previous format"><ChevronUp className="w-3 h-3" /></button>
                                    <button type="button" onClick={() => handleCycleFormat('down')} className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Next format"><ChevronDown className="w-3 h-3" /></button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                      );
                    })}
                    {shadesEnabled && (color.customShades || generateShades(color.hex, shadesCount)).map((shade, idx) => (
                      <Popover key={`shade-${idx}`}>
                        <PopoverTrigger asChild>
                          <div
                            className="w-8 h-8 rounded-full border-2 border-border cursor-pointer hover:scale-110 transition-transform shadow-sm"
                            style={{ backgroundColor: shade.hex }}
                            title={`${color.variable}${shade.variable}: ${getColorValue(shade.hex, format)}`}
                          />
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-4 bg-card border-border text-foreground" side="top">
                          <HexColorPicker
                            color={shade.hex}
                            onChange={(newColor) => {
                              const updatedShades = [...(color.customShades || generateShades(color.hex, shadesCount))];
                              updatedShades[idx] = { ...shade, hex: newColor.toUpperCase() };
                              onColorChange(color.id, { customShades: updatedShades });
                            }}
                            style={{ width: '100%' }}
                          />
                          <div className="mt-2 space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2 flex-1 relative">
                                <Input
                                  value={getColorValue(shade.hex, format)}
                                  onChange={(e) => {
                                    const parsedHex = parseColorValue(e.target.value, format);
                                    if (parsedHex) {
                                      const updatedShades = [...(color.customShades || generateShades(color.hex, shadesCount))];
                                      updatedShades[idx] = { ...shade, hex: parsedHex.toUpperCase() };
                                      onColorChange(color.id, { customShades: updatedShades });
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const parsedHex = parseColorValue(e.target.value, format);
                                    if (!parsedHex) {
                                      e.target.value = getColorValue(shade.hex, format);
                                    }
                                  }}
                                  placeholder={format === 'hex' ? '#000000' : format === 'rgb' ? 'rgb(0, 0, 0)' : 'hsl(0, 0%, 0%)'}
                                  className="font-mono text-sm flex-1 pr-8"
                                />
                                <div className="absolute right-1">
                                  <CopyButton value={getColorValue(shade.hex, format)} />
                                </div>
                              </div>
                              <div className="flex items-center gap-1 bg-muted border border-border rounded-xl pl-2.5 pr-1 py-0.5 h-9 shrink-0 select-none">
                                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground w-12 text-center">
                                  {format}
                                </span>
                                <div className="flex flex-col border-l border-border pl-1.5 h-full justify-center">
                                  <button type="button" onClick={() => handleCycleFormat('up')} className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Previous format"><ChevronUp className="w-3 h-3" /></button>
                                  <button type="button" onClick={() => handleCycleFormat('down')} className="p-0.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground transition-colors" title="Next format"><ChevronDown className="w-3 h-3" /></button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Transparency Section */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={transparencyEnabled}
                  onChange={(e) => {
                    const enabled = e.target.checked;
                    setTransparencyEnabled(enabled);
                    const newTransFormat = format === 'hsl' ? 'hsla' : format === 'rgb' ? 'rgba' : 'hexa';
                    onColorChange(color.id, { 
                      transparencyEnabled: enabled,
                      transparencyCount: enabled ? transparencyCount : undefined,
                      transparencyFormat: enabled ? newTransFormat : undefined
                    });
                  }}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium whitespace-nowrap">
                  Transparent
                </label>
                <Input
                  type="number"
                  min="2"
                  max="10"
                  value={transparencyCount}
                  onChange={(e) => {
                    const count = Math.max(2, parseInt(e.target.value) || 10);
                    setTransparencyCount(count);
                    onColorChange(color.id, { transparencyCount: count });
                  }}
                  className="w-16 h-8 text-sm"
                  disabled={!transparencyEnabled}
                />
              </div>

              {transparencyEnabled && (
                <div className="pt-3 border-t border-border/30">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {generateTransparencies(color.hex, transparencyCount).map((trans, idx) => (
                      <div
                        key={`trans-${idx}`}
                        className="w-8 h-8 rounded-full border-2 border-border relative overflow-hidden shadow-sm"
                        title={`${color.variable}${trans.variable}: ${getTransparencyColorValue(trans.hex, trans.opacity, format)}`}
                        style={{ 
                          background: `linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)`,
                          backgroundSize: '8px 8px',
                          backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                        }}
                      >
                        <div
                          className="w-full h-full"
                          style={{ 
                            backgroundColor: trans.hex,
                            opacity: trans.opacity / 100
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
        </div>
      </div>
    </motion.div>
  );
});

ColorSwatch.displayName = 'ColorSwatch';